import type { VariantProps } from 'class-variance-authority'
import type { badgeVariants } from '@/components/ui/badge'
import type { DisplayStatus, TicketStatus, TicketPriority } from '@/types/ticket'
import type { TrackerStatus, DevStage, TrackerWaitingOn } from '@/types/tracker'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

// Stored statuses, as the staff status select offers them.
export const statusLabels: Record<TicketStatus, string> = {
  open: 'Open',
  on_hold: 'On Hold',
  in_progress: 'In Progress',
  pending: 'Awaiting User',
  resolved: 'Resolved',
  closed: 'Closed',
}

export const priorityLabels: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
}

// Priority pills (V2 table's Priority column) reuse the destructive/neutral
// hues already established for status: High reads as urgent (mockup's
// pill-high, red), Normal/Low are neutral gray (mockup's pill-norm — it has
// no separate Low example, so Low shares Normal's treatment).
export const priorityVariants: Record<TicketPriority, BadgeVariant> = {
  high: 'destructive',
  normal: 'neutral',
  low: 'neutral',
}

// The derived status pill, for staff. A tracked ticket's stored pending is its
// proposal to close, waiting on the client: Awaiting User.
export const displayStatusLabels: Record<DisplayStatus, string> = {
  open: 'Open',
  user_reply: 'User Reply',
  user_reply_awaiting_you: 'User Reply (Awaiting You)',
  in_progress: 'In Progress',
  awaiting_user: 'Awaiting User',
  pending: 'Awaiting User',
  on_hold: 'On Hold',
  resolved: 'Resolved',
  closed: 'Closed',
}

// What a customer sees. A user reply is with us, so it reads Open; they never
// see "User Reply" or "Awaiting You".
export const customerStatusLabels: Record<DisplayStatus, string> = {
  open: 'Open',
  user_reply: 'Open',
  user_reply_awaiting_you: 'Open',
  in_progress: 'In progress',
  awaiting_user: 'Awaiting your reply',
  pending: 'Awaiting your reply',
  on_hold: 'On hold',
  resolved: 'Resolved',
  closed: 'Closed',
}

// Tinted semantic pills; Awaiting You is the loudest.
export const displayStatusVariants: Record<DisplayStatus, BadgeVariant> = {
  open: 'info',
  user_reply: 'warning',
  user_reply_awaiting_you: 'destructive',
  in_progress: 'default',
  awaiting_user: 'neutral',
  pending: 'neutral',
  on_hold: 'neutral',
  resolved: 'success',
  closed: 'neutral',
}

const CUSTOMER_VARIANTS: Partial<Record<DisplayStatus, BadgeVariant>> = {
  user_reply: 'info',
  user_reply_awaiting_you: 'info',
  awaiting_user: 'warning',
  pending: 'warning',
}

export function displayStatusOf(ticket: { status: TicketStatus; displayStatus?: DisplayStatus }): DisplayStatus {
  return ticket.displayStatus ?? ticket.status
}

export function statusPill(ticket: { status: TicketStatus; displayStatus?: DisplayStatus }, audience: 'staff' | 'customer'): { label: string; variant: BadgeVariant } {
  const s = displayStatusOf(ticket)
  return audience === 'staff'
    ? { label: displayStatusLabels[s], variant: displayStatusVariants[s] }
    : { label: customerStatusLabels[s], variant: CUSTOMER_VARIANTS[s] ?? displayStatusVariants[s] }
}

// Mirrors the API's ADMIN_TRANSITIONS: staff move freely between open, on hold, resolved and
// closed. in_progress/pending are pre-migration values, only left.
export const STAFF_SET_STATUSES: TicketStatus[] = ['open', 'on_hold', 'resolved', 'closed']

export const trackerStatusLabels: Record<TrackerStatus, string> = {
  new: 'New',
  needs_info: 'Needs info',
  accepted: 'Accepted',
  proposed_closed: 'Proposed closed',
  closed: 'Closed',
  on_hold: 'On hold',
  wont_do: "Won't do",
}

// Colour follows the mockup's Client Tracker "Status" pills: Accepted=default
// (green tint, matching "In progress" on the Tickets screen), Proposed
// closed=warning(amber). The rest have no direct mockup example; new/closed/
// on_hold read as neutral, needs_info as warning (needs attention), wont_do
// stays destructive.
export const trackerStatusVariants: Record<TrackerStatus, BadgeVariant> = {
  new: 'neutral',
  needs_info: 'warning',
  accepted: 'default',
  proposed_closed: 'warning',
  closed: 'neutral',
  on_hold: 'neutral',
  wont_do: 'destructive',
}

export const devStageLabels: Record<DevStage, string> = {
  not_scoped: 'Not scoped',
  scoped: 'Scoped',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
}

// Colour follows the mockup's Client Tracker "Progress" pills: In
// progress=info(cyan), Shipped=success(lime), Not scoped=neutral(gray).
export const devStageVariants: Record<DevStage, BadgeVariant> = {
  not_scoped: 'neutral',
  scoped: 'neutral',
  planned: 'neutral',
  in_progress: 'info',
  shipped: 'success',
  cancelled: 'destructive',
}

export const trackerStatusOrder: TrackerStatus[] = [
  'new',
  'needs_info',
  'accepted',
  'proposed_closed',
  'closed',
  'on_hold',
  'wont_do',
]

export const waitingOnOrder: TrackerWaitingOn[] = ['etainabl', 'client']

// Who the next step is waiting on, named: the client is shown by its own
// company name, falling back to a generic word when that isn't to hand.
export function waitingOnLabel(waitingOn: TrackerWaitingOn, companyName?: string | null): string {
  return waitingOn === 'etainabl' ? 'Etainabl' : companyName || 'Client'
}

export const waitingOnVariants: Record<TrackerWaitingOn, BadgeVariant> = {
  etainabl: 'info',
  client: 'warning',
}
